import './driverCarding.css'

import { useDriverCards } from '@api/useDriverCards'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { AssignCardsModalFooter } from '@components/DriverManagement/Carding/components/AssignCards/AssignCardsFooter'
import { AssignCardsModal } from '@components/DriverManagement/Carding/components/AssignCards/AssignCardsModal'
import { GraviButton, GraviGrid, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { Modal } from 'antd'
import moment from 'moment'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getColumnDefs } from './components/getColumnDefs'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'driver-terminal-carding'

export function Carding() {
  const [selectedDrivers, setSelectedDrivers] = useState([])
  const [selectedOptions, setSelectedOptions] = useState(null)
  const [selectedValues, setSelectedValues] = useState([])
  const [isReloading, setIsReloading] = useState(false)
  const [popVisible, setPopVisible] = useState(false)
  const [rowData, setRowData] = useState([])
  const gridAPIRef = useRef(null)
  const driversAssignGridAPIRef = useRef(null)
  const { updateSetting } = useUserSettings()
  const {
    getDriverCardsMetadataQuery,
    getDriverCardsOverviewQuery,
    editDriverCardMutation,
    deleteDriverCardMutation,
    addCardsMutation,
  } = useDriverCards()
  const handleDelete = (row) => {
    const request = {
      driver_id: row.driver_id,
      card_id: row.card_id,
      terminal_id: row.terminal_id,
    }
    deleteDriverCardMutation.mutate(request)
  }
  const { data, isPending: isLoadingOverview, isFetching: isFetchingOverview } = getDriverCardsOverviewQuery()
  const {
    data: driverCardsMetadata,
    isPending: isLoadingMetadata,
    isFetching: isFetchingMetadata,
  } = getDriverCardsMetadataQuery()
  const getRowId = useCallback((row) => row?.data?.key, [])

  const columnDefs = useMemo(() => getColumnDefs(handleDelete), [data?.rows, handleDelete])
  const agPropOverrides = useMemo(() => {
    return {
      groupDisplayType: 'multipleColumns',
      autoGroupColumnDef: { minWidth: 200 },
      suppressAggFuncInHeader: true,
      getRowId,
      groupDefaultExpanded: -1,
    }
  }, [data?.rows])
  const handleCloseAssignModal = () => {
    setSelectedOptions(null)
    setSelectedValues([])
    setSelectedDrivers([])
    driversAssignGridAPIRef.current.deselectAll()
    setPopVisible(false)
    setIsReloading(false)
  }
  const handleBulkAdd = async () => {
    setIsReloading(true)
    try {
      const request = {
        driver_ids: selectedDrivers?.map((driver) => driver.driver_id),
        terminal_cards: selectedOptions,
      }
      await addCardsMutation.mutateAsync(request)
      handleCloseAssignModal()
    } catch (e) {
      console.error(e)
    } finally {
      setIsReloading(false)
    }
  }
  const handleUpsertDate = async (row) => {
    const request = {
      terminal_id: row.terminal_id,
      card_id: row.card_id,
      driver_id: row.driver_id,
      certification_date: moment(row.certification_date).format('YYYY-MM-DDT00:00:00'),
    }
    try {
      await editDriverCardMutation.mutateAsync(request)
    } catch (e) {
      console.error(e)
    }
  }
  const excelDownloadOptions = useMemo(
    () => ({
      sheetName: 'Driver Cards',
      fileName: 'Driver Cards.xlsx',
      allColumns: false,
      columnKeys: [
        'depot',
        'driver',
        'terminal',
        'card_name',
        'certification_date',
        'status',
        'expiration_date',
        'days_valid',
        'warning_threshold',
        'activity',
        'certification_trigger',
        'dispatch_impact',
        'market',
      ],
    }),
    []
  )
  useEffect(() => {
    if (window.location.search.includes('key') && gridAPIRef.current) {
      const key = queryString.parse(window.location.search)?.key
      setRowData(data?.rows?.filter((row) => row.key === key))
    } else {
      setRowData(data?.rows)
    }
  }, [data?.rows])

  return (
    <>
      <GraviGrid
        externalRef={gridAPIRef}
        updateEP={handleUpsertDate}
        rowData={rowData || []}
        loading={isLoadingOverview || isFetchingOverview}
        controlBarProps={{
          title: 'Driver Carding',
          actionButtons: (
            <Horizontal>
              <UploadDownloadButtons
                excelOptions={[
                  {
                    title: 'Download Driver Cards',
                    onClick: () => gridAPIRef.current.exportDataAsExcel(excelDownloadOptions),
                  },
                ]}
              />
              <GraviButton
                theme2
                buttonText='Add Cards'
                onClick={() => setPopVisible(true)}
                className='ml-2 mr-4 square-ish'
              />
            </Horizontal>
          ),
        }}
        columnDefs={columnDefs}
        agPropOverrides={agPropOverrides}
        storageKey={STORAGE_KEY}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      <Modal
        destroyOnClose
        className='assign-drivers-modal'
        visible={popVisible}
        onCancel={handleCloseAssignModal}
        title={
          <Texto className='pt-3 pl-4' category='h5'>
            Assign Cards to Drivers
          </Texto>
        }
        footer={
          <AssignCardsModalFooter
            selectedDrivers={selectedDrivers}
            handleCloseAssignModal={handleCloseAssignModal}
            handleBulkAdd={handleBulkAdd}
            isReloading={isReloading}
            selectedCards={selectedOptions}
          />
        }
        width='550px'
      >
        <AssignCardsModal
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          data={driverCardsMetadata}
          setSelectedOptions={setSelectedOptions}
          setSelectedDrivers={setSelectedDrivers}
          driversAssignGridAPIRef={driversAssignGridAPIRef}
          loading={isLoadingMetadata || isFetchingMetadata}
        />
      </Modal>
    </>
  )
}
